import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { GBSCError } from "@/models/GBSCError";
import {
  IAdmittedPatientDetail,
  IDoctorHospitalDetail,
  IDoctorHospitalListResponse,
  IHospitalPatientResponse,
  IHospitalRooms,
  IHospitalTeam,
} from "@/models/ApiRequestResponse/doctor";
import { IAdmitPatientPopUps, IRoomType, ISelectedDate } from "@/models/doctor";
import { IPDashboardSliceType } from "./models/ipDashboardSliceModel";

import {
  getAdmittedPatientDetailApi,
  getDoctorHospitalListApi,
  getDoctorHospitalPatientListApi,
  getHospitalRoomsListApi,
} from "@/utils/apis/doctor/ipDashboard";
import { getHospitalTeamApi } from "@/utils/apis/doctor/profile";

import { RootState } from "@/store/store";

interface IDoctorHospitalPatients {
  hospitalId: string;
  discharged?: boolean;
  start_date?: string;
  end_date?: string;
  room_id?: string;
  room_bed_id?: string;
  keyword?: string;
  page?: number; // Added page for pagination
}
export const fetchDoctorHospitalListing = createAsyncThunk<
  IDoctorHospitalListResponse | GBSCError
>("doctor/fetchDoctorHospitalListing", async () => {
  const response = await getDoctorHospitalListApi();
  return response;
});

export const fetchDoctorHospitalPatientListing = createAsyncThunk(
  "doctor/fetchDoctorHospitalPatientListing",
  async ({
    hospitalId,
    start_date,
    end_date,
    discharged,
    keyword,
    room_id,
    room_bed_id,
    page = 1, // Default page to 1
  }: IDoctorHospitalPatients) => {
    const response = await getDoctorHospitalPatientListApi(
      hospitalId,
      start_date,
      end_date,
      discharged,
      room_id,
      room_bed_id,
      keyword,
      page
    );
    return response;
  }
);

export const fetchSearchDoctorHospitalPatientListing = createAsyncThunk(
  "doctor/fetchSearchDoctorHospitalPatientListing",
  async ({
    hospitalId,
    start_date,
    end_date,
    keyword,
    page = 1,
  }: IDoctorHospitalPatients) => {
    const response = await getDoctorHospitalPatientListApi(
      hospitalId,
      start_date,
      end_date,
      undefined,
      undefined,
      undefined,
      keyword,
      page
    );
    return response;
  }
);

export const fetchHospitalRoomsListing = createAsyncThunk(
  "doctor/fetchHospitalRoomsListing",
  async ({
    hospitalId,
    available,
    isAdmitPatient = false,
  }: {
    hospitalId: string;
    available?: boolean;
    isAdmitPatient?: boolean;
  }) => {
    const response = await getHospitalRoomsListApi(hospitalId, available);
    return response;
  }
);

export const fetchAllHospitalRoomsListing = createAsyncThunk(
  "doctor/fetchAllHospitalRoomsListing",
  async ({
    hospitalId,
    start_date,
    end_date,
  }: {
    hospitalId: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const response = await getHospitalRoomsListApi(
      hospitalId,
      false,
      start_date,
      end_date
    );
    return response;
  }
);

export const fetchAdmitPatientDetails = createAsyncThunk(
  "doctor/fetchAdmitPatientDetails",
  async ({ admitPatientId }: { admitPatientId: string }) => {
    const response = await getAdmittedPatientDetailApi(admitPatientId);
    return response;
  }
);

export const fetchHospitalTeam = createAsyncThunk(
  "doctor/fetchHospitalTeam",
  async ({ hospitalId, search }: { hospitalId?: string; search?: string }) => {
    const response = await getHospitalTeamApi({ hospitalId, search });
    return response;
  }
);

const initialState = {
  hospitals: null as IDoctorHospitalDetail[] | null,
  hospitalPatients: {
    data: [],
    count: 0,
    page: 1,
    hasMore: true,
  } as IHospitalPatientResponse,
  hospitalSearchPatients: {} as IHospitalPatientResponse,
  isHospitalSearchPatientsLoading: false as boolean,
  patientDetail: {} as IAdmittedPatientDetail,
  rooms: {
    allRooms: [] as IHospitalRooms[],
    ward: [] as IHospitalRooms[],
    icu: [] as IHospitalRooms[],
  },
  admitPatientRooms: {
    allRooms: [] as IHospitalRooms[],
    ward: [] as IHospitalRooms[],
    icu: [] as IHospitalRooms[],
  },
  admitPatientPopUps: {
    isAdmitted: false,
    isDischarged: false,
  } as IAdmitPatientPopUps,
  isEditAdmitPatient: false as boolean,
  isReAdmitPatient: false as boolean,
  selectedHospitalId: "",
  isLoading: true as boolean,
  isHospitalLoading: true as boolean,
  isPatientListLoading: true as boolean,
  isRoomLoading: true as boolean,
  isAllRoomLoading: true as boolean,
  selectRoomId: "" as string,
  chipFilter: null as string | null,
  selectedDate: null as ISelectedDate | null,
  totalActivePatientCount: 0 as number,
  isWardIcuColumnShown: true as boolean,
  viewHospitalTeamOpen: false as boolean,
  hospitalTeam: [] as IHospitalTeam[],
  viewHospitalTeamDetail: {} as IHospitalTeam,
  isHospitalTeamLoading: true as boolean,
  viewHospitalTeam: {} as {
    id: string;
    name: string;
  },
} as IPDashboardSliceType;

const ipDashboardSlice = createSlice({
  name: "ipDashboard",
  initialState,
  reducers: {
    setResetIPDashboard: () => initialState,
    setSelectedHospitalId: (state, action) => {
      state.selectedHospitalId = action.payload;
    },
    setEditAdmitPatient: (state, action) => {
      state.isEditAdmitPatient = action.payload;
    },
    setReAdmitPatient: (state, action) => {
      state.isReAdmitPatient = action.payload;
    },
    resetAdmitPatientDetail: (state) => {
      state.patientDetail = {} as IAdmittedPatientDetail;
    },
    setOpenCloseAdmittedDischargeModal: (
      state: IPDashboardSliceType,
      action
    ) => {
      const key: keyof IAdmitPatientPopUps = action.payload;
      state.admitPatientPopUps = {
        ...state.admitPatientPopUps,
        [action.payload]: !state.admitPatientPopUps[key],
      };
    },
    resetAdmitPatients: (state) => {
      state.hospitalPatients = {
        data: [],
        count: 0,
        page: 1,
        hasMore: true,
      };
    },
    resetSearchAdmittedPatients: (state) => {
      state.hospitalSearchPatients = {
        data: [],
        count: 0,
        page: 1,
        hasMore: true,
      };
    },
    setSelectRoomId: (state, action) => {
      state.selectRoomId = action.payload;
    },
    setChipFilter: (state, action) => {
      state.chipFilter = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setTotalActivePatientCount: (state, action) => {
      state.totalActivePatientCount = action.payload;
    },
    setIsWardIcuColumnShown: (state) => {
      state.isWardIcuColumnShown = !state.isWardIcuColumnShown;
    },
    setViewHospitalTeamOpen: (state, action) => {
      state.viewHospitalTeamOpen = action.payload;
    },
    setViewHospitalTeam: (state, action) => {
      state.viewHospitalTeam = action.payload;
    },
    setViewHospitalTeamDetail: (state, action) => {
      state.viewHospitalTeamDetail = action.payload;
    },
    resetIPdashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // doctor hospital listing
      .addCase(fetchDoctorHospitalListing.pending, (state) => {
        state.isHospitalLoading = true;
      })
      .addCase(fetchDoctorHospitalListing.fulfilled, (state, action) => {
        state.isHospitalLoading = false;
        if ("result" in action.payload) {
          state.hospitals = action.payload.result;
          state.selectedHospitalId =
            state.selectedHospitalId || action.payload.result[0]?.hospital_id;
        }
      })
      .addCase(fetchDoctorHospitalListing.rejected, (state) => {
        state.isHospitalLoading = false;
      })
      //  doctor hospital patient listing
      .addCase(fetchDoctorHospitalPatientListing.pending, (state, action) => {
        // Only show full page loader on first page load
        if (action.meta.arg.page === 1) {
          state.isPatientListLoading = true;
        }
      })
      .addCase(fetchDoctorHospitalPatientListing.fulfilled, (state, action) => {
        state.isPatientListLoading = false;
        if ("result" in action.payload) {
          const { page = 1 } = action.meta.arg;
          const { data, count } = action.payload.result;

          if (page === 1) {
            // If it's the first page, replace the list
            state.hospitalPatients.data = data;
          } else {
            // For subsequent pages, append to the list
            state.hospitalPatients.data.push(...data);
          }

          state.hospitalPatients.count = count;
          state.hospitalPatients.page = page;
          state.hospitalPatients.hasMore =
            state.hospitalPatients.data.length < count;
        }
      })
      .addCase(fetchDoctorHospitalPatientListing.rejected, (state) => {
        state.isPatientListLoading = false;
      })
      // doctor hospital search patient listing
      .addCase(fetchSearchDoctorHospitalPatientListing.pending, (state) => {
        state.isHospitalSearchPatientsLoading = true;
      })
      .addCase(
        fetchSearchDoctorHospitalPatientListing.fulfilled,
        (state, action) => {
          state.isHospitalSearchPatientsLoading = false;
          if ("result" in action.payload) {
            state.hospitalSearchPatients = action.payload.result;
          }
        }
      )
      .addCase(fetchSearchDoctorHospitalPatientListing.rejected, (state) => {
        state.isHospitalSearchPatientsLoading = false;
      })

      //  hospital room listing
      .addCase(fetchHospitalRoomsListing.pending, (state) => {
        state.isRoomLoading = true;
      })
      .addCase(fetchHospitalRoomsListing.fulfilled, (state, action) => {
        state.isRoomLoading = false;

        if ("result" in action.payload) {
          if (action.meta.arg.isAdmitPatient) {
            state.admitPatientRooms.allRooms = action.payload.result;
            state.admitPatientRooms.icu = action.payload.result.filter(
              (room) => room.type === IRoomType.ICU
            );
            state.admitPatientRooms.ward = action.payload.result.filter(
              (room) => room.type === IRoomType.WARD
            );
          } else {
            state.rooms.allRooms = action.payload.result;
            state.rooms.icu = action.payload.result.filter(
              (room, index) => room.type === IRoomType.ICU
            );
            state.rooms.ward = action.payload.result.filter(
              (room, index) => room.type === IRoomType.WARD
            );
          }
        }
      })
      .addCase(fetchHospitalRoomsListing.rejected, (state) => {
        state.isRoomLoading = false;
      })
      //  hospital all room listing
      .addCase(fetchAllHospitalRoomsListing.pending, (state) => {
        state.isAllRoomLoading = true;
      })
      .addCase(fetchAllHospitalRoomsListing.fulfilled, (state, action) => {
        state.isAllRoomLoading = false;

        if ("result" in action.payload) {
          state.rooms.allRooms = action.payload.result;
          state.rooms.icu = action.payload.result.filter(
            (room, index) => room.type === IRoomType.ICU
          );
          state.rooms.ward = action.payload.result.filter(
            (room, index) => room.type === IRoomType.WARD
          );
        }
      })
      .addCase(fetchAllHospitalRoomsListing.rejected, (state) => {
        state.isAllRoomLoading = false;
      })
      //  admit patient detail
      .addCase(fetchAdmitPatientDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdmitPatientDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.patientDetail = action.payload.result;
        }
      })
      .addCase(fetchAdmitPatientDetails.rejected, (state) => {
        state.isLoading = false;
      })
      //  hospital team
      .addCase(fetchHospitalTeam.pending, (state) => {
        state.isHospitalTeamLoading = true;
      })
      .addCase(fetchHospitalTeam.fulfilled, (state, action) => {
        state.isHospitalTeamLoading = false;
        if ("result" in action.payload) {
          state.hospitalTeam = action.payload.result;
        }
      })
      .addCase(fetchHospitalTeam.rejected, (state) => {
        state.isHospitalTeamLoading = false;
      });
  },
});

export const ipDashboardRootSelector = (state: RootState) => state.ipDashboard;

export const ipDashboardDetails = createSelector(
  [ipDashboardRootSelector],
  (state) => {
    return {
      patientDetail: state.patientDetail,
      hospitals: state.hospitals,
      hospitalTeam: state.hospitalTeam,
      viewHospitalTeam: state.viewHospitalTeam,
      isHospitalTeamLoading: state.isHospitalTeamLoading,
      viewHospitalTeamOpen: state.viewHospitalTeamOpen,
      isLoading: state.isLoading,
      isHospitalLoading: state.isHospitalLoading,
      isPatientListLoading: state.isPatientListLoading,
      isRoomLoading: state.isRoomLoading,
      isAllRoomLoading: state.isAllRoomLoading,
      selectRoomId: state.selectRoomId,
      chipFilter: state.chipFilter,
      selectedDate: state.selectedDate,
      totalActivePatientCount: state.totalActivePatientCount,
      isWardIcuColumnShown: state.isWardIcuColumnShown,
      selectedHospitalId: state.selectedHospitalId,
      isEditAdmitPatient: state.isEditAdmitPatient,
      isReAdmitPatient: state.isReAdmitPatient,
      admitPatientPopUps: state.admitPatientPopUps,
      rooms: state.rooms,
      admitPatientRooms: state.admitPatientRooms,
      hospitalPatients: state.hospitalPatients,
      viewHospitalTeamDetail: state.viewHospitalTeamDetail,
      hospitalSearchPatients: state.hospitalSearchPatients,
      isHospitalSearchPatientsLoading: state.isHospitalSearchPatientsLoading,
    };
  }
);

export const {
  setSelectRoomId,
  resetAdmitPatients,
  setSelectedHospitalId,
  setEditAdmitPatient,
  setReAdmitPatient,
  resetAdmitPatientDetail,
  setOpenCloseAdmittedDischargeModal,
  setChipFilter,
  setSelectedDate,
  setTotalActivePatientCount,
  setIsWardIcuColumnShown,
  resetIPdashboard,
  setResetIPDashboard,
  setViewHospitalTeamOpen,
  setViewHospitalTeam,
  setViewHospitalTeamDetail,
  resetSearchAdmittedPatients,
} = ipDashboardSlice.actions;
export default ipDashboardSlice.reducer;
